package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminSectionDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Static Section Editor Component (T086, T091, T040, T041)
 *
 * Features:
 * - T086: Vendor ID input field
 * - T091: Vendor ID validation (non-empty, alphanumeric + hyphens)
 * - T040: Async vendor_id availability check on blur
 * - T041: Display vendor_id conflict errors with deletion dates
 */
@Composable
fun StaticSectionEditor(
    section: AdminSectionDto?,
    onUpdate: (String, String) -> Unit = { _, _ -> },
    onCheckVendorId: (suspend (String) -> String?)? = null
) {
    val colors = AppTheme.colors

    // State for static section fields
    var internalName by remember { mutableStateOf(section?.internalName ?: "") }
    var vendorId by remember { mutableStateOf(section?.vendorId ?: "") }
    var vendorIdError by remember { mutableStateOf<String?>(null) }
    var vendorIdConflictError by remember { mutableStateOf<String?>(null) }
    var checkingVendorId by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // T091: Vendor ID validation function
    fun validateVendorId(value: String): String? {
        return when {
            value.isBlank() -> "Vendor ID is required"
            !value.matches(Regex("^[a-zA-Z0-9-]+$")) -> "Only alphanumeric characters and hyphens allowed"
            value.length < 3 -> "Vendor ID must be at least 3 characters"
            value.length > 50 -> "Vendor ID must not exceed 50 characters"
            else -> null
        }
    }

    // Update parent when values change; clear conflict error when vendor_id is edited
    LaunchedEffect(internalName, vendorId) {
        vendorIdConflictError = null
        if (vendorIdError == null && vendorId.isNotBlank()) {
            onUpdate(internalName, vendorId)
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            themeTransition()
        }
    }) {

        // Internal name
        Div({ style { marginBottom(Spacing.lg) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                Text("Internal Name")
            }
            Input(InputType.Text) {
                attr("type", "text")
                attr("value", internalName)
                attr("placeholder", "e.g., Vendor Widget Section")
                onInput { internalName = it.value }
                style {
                    width(100.percent)
                    padding(Spacing.sm)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    property("box-sizing", "border-box")
                }
            }
        }

        // Vendor ID input
        Div({ style { marginBottom(Spacing.lg) } }) {
            Label(attrs = { style { display(DisplayStyle.Block); marginBottom(Spacing.xs); fontWeight("bold"); color(Color(colors.text)) } }) {
                Text("Vendor ID")
                Span({ style { color(Color(colors.danger)); marginLeft(Spacing.xs) } }) { Text("*") }
            }
            Input(InputType.Text) {
                attr("type", "text")
                attr("value", vendorId)
                attr("placeholder", "e.g., vendor-widget-123")
                onInput {
                    vendorId = it.value
                    vendorIdError = validateVendorId(it.value)
                }
                onBlur {
                    // T040: Check vendor_id availability when user leaves the field
                    val localError = validateVendorId(vendorId)
                    if (localError == null && vendorId.isNotBlank() && onCheckVendorId != null) {
                        scope.launch {
                            checkingVendorId = true
                            vendorIdConflictError = try {
                                onCheckVendorId(vendorId)
                            } catch (e: Exception) {
                                null // Don't block UX if availability check fails
                            } finally {
                                checkingVendorId = false
                            }
                        }
                    }
                }
                style {
                    width(100.percent)
                    padding(Spacing.sm)
                    border(1.px, LineStyle.Solid, if (vendorIdError != null || vendorIdConflictError != null) Color(colors.danger) else Color(colors.border))
                    borderRadius(BorderRadius.sm)
                    fontFamily("monospace")
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    property("box-sizing", "border-box")
                }
            }
            vendorIdError?.let { err ->
                Div({ style { fontSize(Typography.sm); color(Color(colors.danger)); marginTop(Spacing.xs) } }) {
                    Text(err)
                }
            }
            // T041: Display async vendor_id conflict errors (with deletion dates)
            vendorIdConflictError?.let { err ->
                Div({ style { fontSize(Typography.sm); color(Color(colors.danger)); marginTop(Spacing.xs) } }) {
                    Text(err)
                }
            }
            if (checkingVendorId) {
                Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                    Text("Checking availability...")
                }
            }
        }
    }
}
